import { Directive, ElementRef, Input, OnDestroy, OnInit, forwardRef, NgZone } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
  selector: '[appSummernote]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SummernoteDirective),
      multi: true
    }
  ]
})
export class SummernoteDirective implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() set height(value: number) {
    this._height = value;
    // if (this.isInitialized) {
    //   this.jq(this.el.nativeElement).summernote('height', value);
    // }
    this.updateHeight();
  }
  private _height = 200;

  @Input() placeholder = '';
  @Input() maxLength: number | null = null;

  private isInitialized = false;
  private lastValidCode = '';
  private onChange: any = () => {};
  private onTouched: any = () => {};

  constructor(private el: ElementRef, private ngZone: NgZone) {}

  private get jq(): any {
    return (window as any)['jQuery'] || (window as any)['$'];
  }

  private updateHeight() {
  if (this.isInitialized) {
    // Summernote uses .summernote('height', value)
    // or you can target the editable area directly via CSS if needed
    this.jq(this.el.nativeElement).summernote('height', this._height);
  }
}

  ngOnInit() {
    const jq = this.jq;
    if (!jq || !jq.fn || !jq.fn['summernote']) {
      console.error('[SummernoteDirective] jQuery or Summernote plugin not found on window. Ensure jquery and summernote scripts are loaded before the Angular app.');
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      jq(this.el.nativeElement).summernote({
        height: this._height,
        placeholder: this.placeholder,
        toolbar: [
          ['style', ['style']],
          ['font', ['bold', 'italic', 'underline']]
        ],
        styleTags: ['p', 'h1', 'h2', 'h3'],
        callbacks: {
          onChange: (contents: string) => {
            this.ngZone.run(() => {
              if (this.maxLength !== null && this.getPlainTextLength(contents) > this.maxLength) {
                jq(this.el.nativeElement).summernote('code', this.lastValidCode);
                return;
              }
              this.lastValidCode = contents;
              this.onChange(contents);
            });
          },
          onBlur: () => {
            this.ngZone.run(() => {
              this.onTouched();
            });
          }
        }
      });

      this.isInitialized = true;

      if (this.initialValue !== null) {
        this.lastValidCode = this.initialValue;
        jq(this.el.nativeElement).summernote('code', this.initialValue);
      }
    });
  }

  ngOnDestroy() {
    if (this.isInitialized) {
      const jq = this.jq;
      // Summernote's destroy calls hideDialog which expects Bootstrap's
      // jQuery .modal() plugin. Since we use ng-bootstrap (no jQuery modal),
      // stub it temporarily to prevent "t.modal is not a function" errors.
      const hadModal = !!jq.fn.modal;
      if (!hadModal) {
        jq.fn.modal = function () { return this; };
      }
      jq(this.el.nativeElement).summernote('destroy');
      if (!hadModal) {
        delete jq.fn.modal;
      }
    }
  }

  private getPlainTextLength(html: string): number {
    if (!html) return 0;
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent?.length ?? 0;
  }

  writeValue(value: any) {
    const code = value || '';
    this.lastValidCode = code;
    if (this.isInitialized) {
      if (this.jq(this.el.nativeElement).summernote('code') !== code) {
        this.jq(this.el.nativeElement).summernote('code', code);
        this.resetToolbarState();
      }
    } else {
      this.initialValue = code;
    }
  }

  private resetToolbarState() {
    const jq = this.jq;
    const editor = jq(this.el.nativeElement).closest('.note-editor');

    // Remove active state from all toolbar buttons
    editor.find('.note-btn.active').removeClass('active');

    // Reset the style dropdown to show the default "Normal" label
    editor.find('.note-current-style-block').text('Normal');

    // Clear any lingering selection so the toolbar reflects
    // the actual cursor context on the next focus
    const editable = editor.find('.note-editable')[0];
    if (editable) {
      const sel = window.getSelection();
      if (sel && editable.contains(sel.anchorNode)) {
        sel.removeAllRanges();
      }
    }
  }

  private initialValue: string | null = null;

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.isInitialized) {
      if (isDisabled) {
        this.jq(this.el.nativeElement).summernote('disable');
      } else {
        this.jq(this.el.nativeElement).summernote('enable');
      }
    }
  }

  resetHeight(newHeight: number = 300) {
  if (this.isInitialized) {
    this._height = newHeight;
    const jq = this.jq;
    const element = jq(this.el.nativeElement);
    element.summernote('height', newHeight);
    const editor = element.next('.note-editor');
    if (editor.length) {
      editor.find('.note-editable').css('height', newHeight + 'px');
    }
  }
}
}
